import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import VSLogo from "@/components/VSLogo";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, saveAuth } from "@/lib/api";
import { toast } from "sonner";
import { Shield, ArrowRight, Lock } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveAuth(data.token, data.user);
      toast.success("Welcome back");
      if (next) nav(next);
      else if (data.user.role === "candidate") nav("/candidate/hub");
      else if (data.user.role === "recruiter") nav("/recruiter/dashboard");
      else if (data.user.role === "hr_admin") nav("/admin/dashboard");
      else if (data.user.role === "super_admin") nav("/admin/dashboard");
      else nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex vs-hero-gradient text-white p-12 flex-col justify-between relative overflow-hidden">
        <VSLogo dark size={44} showWordmark className="text-white"/>
        <div className="relative">
          <h2 className="font-display text-4xl font-bold leading-tight">Welcome back to VS-Solutions Careers</h2>
          <p className="mt-4 text-slate-300 max-w-md">Sign in to track your applications, assessments, and interviews.</p>
        </div>
        <div className="text-xs text-slate-400">© 2026 Vijay Software Solutions Pvt. Ltd.</div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8"><VSLogo showWordmark/></div>
          <h1 className="font-display text-3xl font-bold text-[#0A2540]">Candidate Sign In</h1>
          <p className="text-slate-500 mt-2">Access your candidate dashboard.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={e=>setEmail(e.target.value)} data-testid="login-email"/>
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" required value={password} onChange={e=>setPassword(e.target.value)} data-testid="login-password"/>
            </div>
            <Button disabled={loading} type="submit" className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="login-submit">
              {loading ? "Signing in..." : "Sign in"} <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500">New here? <Link to="/register" className="text-[#2563EB] font-medium" data-testid="login-register-link">Create an account</Link></p>

          <Card className="mt-10 bg-slate-900 border-slate-800 p-6 text-white overflow-hidden relative" data-testid="management-login-card">
            <div className="absolute -right-10 -top-10 w-40 h-40 bg-[#F59E0B]/10 rounded-full blur-2xl"/>
            <div className="relative">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/15 border border-[#F59E0B]/30 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#F59E0B]"/>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] font-semibold">Authorized Access Only</div>
              </div>
              <h3 className="mt-3 font-display text-xl font-semibold">VS Management Login</h3>
              <p className="text-slate-400 text-sm mt-1">Secure access for authorized VS-Solutions management.</p>
              <Button asChild className="mt-4 bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="goto-admin-login">
                <Link to="/admin/login"><Lock className="w-4 h-4 mr-2"/>Access Management Portal</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
